import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { MaTaxRegistration, ResolvedRates } from '@/api/ma-tax-schemas'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatRate } from '@/lib/ma-tax-format'

type FormState = Omit<MaTaxRegistration, 'property_id' | 'updated_at' | 'updated_by'>

const EMPTY: FormState = {
  jurisdiction: 'ma',
  certificate_id: null,
  operator_name: null,
  street: null,
  city: null,
  zip: null,
  municipality_code: null,
  community_impact_fee_applies: null,
}

function RatesPanel({ rates }: { rates: ResolvedRates }) {
  const combined =
    rates.state + rates.local + rates.water_protection + rates.convention_center + rates.tourism
  const rows: [string, string][] = [
    ['State room occupancy', formatRate(rates.state)],
    ['Local option', formatRate(rates.local)],
    [
      'Cape Cod & Islands Water Protection Fund',
      rates.water_protection ? formatRate(rates.water_protection) : 'not in this town',
    ],
    [
      'Convention Center Fund',
      rates.convention_center ? formatRate(rates.convention_center) : 'not in this town',
    ],
    [
      'Community impact fee (town rate)',
      rates.community_impact_adopted ? formatRate(rates.community_impact_adopted) : 'not adopted',
    ],
    ['Tourism fee', rates.tourism ? formatRate(rates.tourism) : 'none'],
  ]
  return (
    <div className="rounded-card border border-border bg-surface2 p-4">
      <div className="text-sm font-medium text-text">{rates.municipality_name} rates</div>
      <div className="mb-2 text-xs text-textFaint">
        Read-only, from the DOR municipal rate table in admin-api, as of {rates.date}.
      </div>
      <dl className="divide-y divide-border text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-2">
            <dt className="text-textMuted">{label}</dt>
            <dd className="tabular-nums text-text">{value}</dd>
          </div>
        ))}
        <div className="flex justify-between gap-4 py-2 font-medium">
          <dt>Combined, before any impact fee</dt>
          <dd className="tabular-nums">{formatRate(combined)}</dd>
        </div>
      </dl>
    </div>
  )
}

interface MaTaxCardProps {
  propertyId: string
  isActive: boolean
}

/**
 * The property's Massachusetts room occupancy registration (CAP-548): what its
 * Form ROC row needs. Rates are never edited here - they come from the town,
 * in admin-api's code-reviewed table - so this card only says which town.
 */
export function MaTaxCard({ propertyId, isActive }: MaTaxCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<FormState>(EMPTY)
  const [saving, setSaving] = useState(false)

  const query = useQuery({
    queryKey: ['ma-tax-registration', propertyId],
    queryFn: () => api.getMaTaxRegistration(propertyId),
    enabled: !!propertyId && isActive,
    staleTime: 30000,
  })
  const towns = useQuery({
    queryKey: ['ma-tax-municipalities'],
    queryFn: () => api.getMaTaxMunicipalities(),
    enabled: isActive,
    staleTime: 60 * 60 * 1000,
  })

  // Seed once per property so a background refetch cannot wipe an unsaved edit.
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (query.data && seededFor.current !== propertyId) {
      const r = query.data.registration
      setForm(
        r
          ? {
              jurisdiction: r.jurisdiction,
              certificate_id: r.certificate_id,
              operator_name: r.operator_name,
              street: r.street,
              city: r.city,
              zip: r.zip,
              municipality_code: r.municipality_code,
              community_impact_fee_applies: r.community_impact_fee_applies,
            }
          : EMPTY
      )
      seededFor.current = propertyId
    }
  }, [query.data, propertyId])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))
  const text = (key: keyof FormState) => (form[key] as string | null) ?? ''

  const onSave = async () => {
    setSaving(true)
    try {
      const body: FormState =
        form.jurisdiction === 'not_ma' ? { ...EMPTY, jurisdiction: 'not_ma' } : form
      const result = await api.updateMaTaxRegistration(propertyId, body)
      queryClient.setQueryData(['ma-tax-registration', propertyId], result)
      await queryClient.invalidateQueries({ queryKey: ['roc-preview'] })
      toast({
        title: 'Tax registration saved',
        description: result.problems.length
          ? `Still missing: ${result.problems.join('; ')}`
          : 'This property is ready for Form ROC.',
      })
    } catch (err: unknown) {
      toast({
        title: 'Failed to save tax registration',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const isMa = form.jurisdiction === 'ma'
  const town = towns.data?.municipalities.find(m => m.code === form.municipality_code)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Massachusetts tax registration</CardTitle>
        <CardDescription>
          Used for this property&apos;s Form ROC row. Every field is required; nothing is guessed
          from other data.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <LoadingSkeleton className="h-40 w-full" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : (
          <div className="grid gap-6 xl:grid-cols-3">
            <div className="space-y-5 xl:col-span-2">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-text">Tax jurisdiction</legend>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <label className="flex min-h-[44px] items-center gap-2">
                    <input
                      type="radio"
                      name={`jurisdiction-${propertyId}`}
                      checked={isMa}
                      onChange={() => set('jurisdiction', 'ma')}
                    />
                    Massachusetts, reported on Form ROC
                  </label>
                  <label className="flex min-h-[44px] items-center gap-2">
                    <input
                      type="radio"
                      name={`jurisdiction-${propertyId}`}
                      checked={!isMa}
                      onChange={() => set('jurisdiction', 'not_ma')}
                    />
                    Not a Massachusetts property
                  </label>
                </div>
              </fieldset>

              {isMa && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor={`cert-${propertyId}`}>Certificate ID</Label>
                    <Input
                      id={`cert-${propertyId}`}
                      className="font-mono"
                      placeholder="C0000000000"
                      value={text('certificate_id')}
                      onChange={e => set('certificate_id', e.target.value || null)}
                    />
                    <p className="text-xs text-textFaint">C followed by 10 digits.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`operator-${propertyId}`}>Operator name</Label>
                    <Input
                      id={`operator-${propertyId}`}
                      placeholder="As on the certificate"
                      value={text('operator_name')}
                      onChange={e => set('operator_name', e.target.value || null)}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor={`street-${propertyId}`}>Street</Label>
                    <Input
                      id={`street-${propertyId}`}
                      value={text('street')}
                      onChange={e => set('street', e.target.value || null)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`city-${propertyId}`}>City (mailing)</Label>
                    <Input
                      id={`city-${propertyId}`}
                      value={text('city')}
                      onChange={e => set('city', e.target.value || null)}
                    />
                    <p className="text-xs text-textFaint">
                      Village or post-office name, e.g. Hyannis.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`zip-${propertyId}`}>ZIP</Label>
                    <Input
                      id={`zip-${propertyId}`}
                      className="font-mono"
                      inputMode="numeric"
                      placeholder="02601"
                      value={text('zip')}
                      onChange={e => set('zip', e.target.value || null)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`town-${propertyId}`}>Municipality (tax locality)</Label>
                    <Select
                      value={form.municipality_code ?? undefined}
                      onValueChange={value => set('municipality_code', value)}
                    >
                      <SelectTrigger id={`town-${propertyId}`}>
                        <SelectValue placeholder="Choose the town" />
                      </SelectTrigger>
                      <SelectContent>
                        {(towns.data?.municipalities ?? []).map(m => (
                          <SelectItem key={m.code} value={m.code}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-textFaint">
                      Hyannis, Osterville and Marstons Mills are villages of Barnstable.
                    </p>
                  </div>
                  <fieldset className="space-y-1.5">
                    <legend className="text-sm font-medium text-text">Community impact fee</legend>
                    <div className="flex gap-6 text-sm">
                      <label className="flex min-h-[44px] items-center gap-2">
                        <input
                          type="radio"
                          name={`cif-${propertyId}`}
                          checked={form.community_impact_fee_applies === true}
                          onChange={() => set('community_impact_fee_applies', true)}
                        />
                        Applies
                      </label>
                      <label className="flex min-h-[44px] items-center gap-2">
                        <input
                          type="radio"
                          name={`cif-${propertyId}`}
                          checked={form.community_impact_fee_applies === false}
                          onChange={() => set('community_impact_fee_applies', false)}
                        />
                        Does not apply
                      </label>
                    </div>
                    <p className="text-xs text-textFaint">
                      Must be chosen. It depends on the operator&apos;s units in the town, not on
                      Capehost managing them.
                    </p>
                  </fieldset>
                </div>
              )}

              {query.data && query.data.problems.length > 0 && (
                <ul className="space-y-1 text-sm text-amber" aria-label="Missing for Form ROC">
                  {query.data.problems.map(p => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}

              <div className="flex justify-end">
                <Button onClick={onSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>

            <div>
              {isMa && town?.rates_today ? (
                <RatesPanel rates={town.rates_today} />
              ) : isMa ? (
                <p className="text-sm text-textFaint">Choose a town to see its rates.</p>
              ) : (
                <p className="text-sm text-textFaint">Not reported on the Massachusetts return.</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
